import { useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { EditorContent, useEditor, useEditorState } from '@tiptap/react';
import type { Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import { TableKit } from '@tiptap/extension-table';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import Highlight from '@tiptap/extension-highlight';
import toast from 'react-hot-toast';
import {
    PiArrowArcLeftBold,
    PiArrowArcRightBold,
    PiImageBold,
    PiLinkBold,
    PiListBulletsBold,
    PiListNumbersBold,
    PiMinusBold,
    PiQuotesBold,
    PiSmileyBold,
    PiTableBold,
    PiTextAlignCenterBold,
    PiTextAlignLeftBold,
    PiTextAlignRightBold,
    PiTextBBold,
    PiTextItalicBold,
    PiTextStrikethroughBold,
    PiTextUnderlineBold,
    PiHighlighterBold,
    PiSquaresFourBold,
} from 'react-icons/pi';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';
import { useNavigationMeta } from '@v2/hooks/useResourceCrud';
import { AI_BUTTON, AiPlateIcon, useAiStatus } from '@v2/features/ai/aiShared';
import DocAiModal from './DocAiModal';

const ICONS = ['📦', '🚢', '🏭', '🚚', '🏬', '✅', '⚠️', '❗', '💡', '📌', '🧾', '💰', '💵', '⏱️', '📅', '🔍', '📸', '🏷️', '📋', '👥', '🔒', '➡️', '1️⃣', '2️⃣', '3️⃣', '4️⃣', '5️⃣'];

type Props = {
    value: string;
    onChange: (html: string) => void;
    /** Título e id do documento: contexto para a IA. */
    title?: string;
    documentId?: number;
};

function ToolButton({
    onClick,
    active,
    disabled,
    title,
    children,
}: {
    onClick: () => void;
    active?: boolean;
    disabled?: boolean;
    title: string;
    children: ReactNode;
}) {
    return (
        <button
            type="button"
            title={title}
            aria-label={title}
            aria-pressed={active}
            disabled={disabled}
            onMouseDown={(e) => e.preventDefault()}
            onClick={onClick}
            className={cn(
                'flex h-8 min-w-8 items-center justify-center rounded-md px-1.5 text-sm text-gray-600 transition hover:bg-gray-100 disabled:opacity-30 dark:text-gray-300 dark:hover:bg-gray-700',
                active && 'bg-primary/10 text-primary dark:bg-primary/20',
            )}
        >
            {children}
        </button>
    );
}

const Divider = () => <span className="mx-0.5 h-5 w-px bg-gray-200 dark:bg-gray-700" />;

function Toolbar({ editor, title, documentId }: { editor: Editor; title: string; documentId?: number }) {
    const fileRef = useRef<HTMLInputElement>(null);
    const [panel, setPanel] = useState<'icons' | 'link' | 'feature' | null>(null);
    const [linkUrl, setLinkUrl] = useState('');
    const [uploading, setUploading] = useState(false);
    const [aiOpen, setAiOpen] = useState(false);
    const nav = useNavigationMeta();
    const ai = useAiStatus();
    const s = useEditorState({
        editor,
        selector: ({ editor: e }) => ({
            bold: e.isActive('bold'),
            italic: e.isActive('italic'),
            underline: e.isActive('underline'),
            strike: e.isActive('strike'),
            highlight: e.isActive('highlight'),
            h2: e.isActive('heading', { level: 2 }),
            h3: e.isActive('heading', { level: 3 }),
            bullet: e.isActive('bulletList'),
            ordered: e.isActive('orderedList'),
            quote: e.isActive('blockquote'),
            link: e.isActive('link'),
            table: e.isActive('table'),
            left: e.isActive({ textAlign: 'left' }),
            center: e.isActive({ textAlign: 'center' }),
            right: e.isActive({ textAlign: 'right' }),
            canUndo: e.can().undo(),
            canRedo: e.can().redo(),
        }),
    });

    const uploadImage = async (file: File) => {
        setUploading(true);
        try {
            const form = new FormData();
            form.append('image', file);
            const { data } = await api.post<{ data: { url: string } }>('/intranet/document-images', form);
            editor.chain().focus().setImage({ src: data.data.url, alt: file.name }).run();
        } catch (err) {
            const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
            toast.error(msg ?? 'Não foi possível enviar a imagem.');
        } finally {
            setUploading(false);
        }
    };

    const applyLink = () => {
        const url = linkUrl.trim();
        if (url === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
        } else {
            const href = /^(https?:\/\/|mailto:|\/)/.test(url) ? url : `https://${url}`;
            editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
        }
        setPanel(null);
    };

    const insertFeature = (name: string, href: string) => {
        const path = href.replace(/^https?:\/\/[^/]+/, '').replace(/^\/v2(?=\/|$)/, '') || '/';
        const { empty } = editor.state.selection;
        if (empty) {
            editor.chain().focus().insertContent({ type: 'text', text: `🔗 ${name}`, marks: [{ type: 'link', attrs: { href: path } }] }).insertContent(' ').run();
        } else {
            editor.chain().focus().setLink({ href: path }).run();
        }
        setPanel(null);
    };

    const heading = (level: 2 | 3) => editor.chain().focus().toggleHeading({ level }).run();

    return (
        <div className="sticky top-0 z-10 border-b border-gray-200 bg-white/95 backdrop-blur dark:border-gray-700 dark:bg-gray-900/95">
            <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5">
                {ai.data?.features.documents && (
                    <>
                        <button
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => setAiOpen(true)}
                            className={cn(AI_BUTTON, 'me-1 h-8 px-2.5 text-xs')}
                        >
                            <AiPlateIcon className="size-4" /> Escrever com IA
                        </button>
                        <Divider />
                    </>
                )}
                <ToolButton title="Desfazer" onClick={() => editor.chain().focus().undo().run()} disabled={!s.canUndo}>
                    <PiArrowArcLeftBold />
                </ToolButton>
                <ToolButton title="Refazer" onClick={() => editor.chain().focus().redo().run()} disabled={!s.canRedo}>
                    <PiArrowArcRightBold />
                </ToolButton>
                <Divider />
                <ToolButton title="Título" active={s.h2} onClick={() => heading(2)}>
                    <span className="text-xs font-bold">T1</span>
                </ToolButton>
                <ToolButton title="Subtítulo" active={s.h3} onClick={() => heading(3)}>
                    <span className="text-xs font-bold">T2</span>
                </ToolButton>
                <Divider />
                <ToolButton title="Negrito" active={s.bold} onClick={() => editor.chain().focus().toggleBold().run()}>
                    <PiTextBBold />
                </ToolButton>
                <ToolButton title="Itálico" active={s.italic} onClick={() => editor.chain().focus().toggleItalic().run()}>
                    <PiTextItalicBold />
                </ToolButton>
                <ToolButton title="Sublinhado" active={s.underline} onClick={() => editor.chain().focus().toggleUnderline().run()}>
                    <PiTextUnderlineBold />
                </ToolButton>
                <ToolButton title="Riscado" active={s.strike} onClick={() => editor.chain().focus().toggleStrike().run()}>
                    <PiTextStrikethroughBold />
                </ToolButton>
                <ToolButton title="Marca-texto" active={s.highlight} onClick={() => editor.chain().focus().toggleHighlight().run()}>
                    <PiHighlighterBold />
                </ToolButton>
                <Divider />
                <ToolButton title="Lista" active={s.bullet} onClick={() => editor.chain().focus().toggleBulletList().run()}>
                    <PiListBulletsBold />
                </ToolButton>
                <ToolButton title="Lista numerada (passo a passo)" active={s.ordered} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
                    <PiListNumbersBold />
                </ToolButton>
                <ToolButton title="Caixa de destaque (dica/atenção)" active={s.quote} onClick={() => editor.chain().focus().toggleBlockquote().run()}>
                    <PiQuotesBold />
                </ToolButton>
                <Divider />
                <ToolButton title="Alinhar à esquerda" active={s.left} onClick={() => editor.chain().focus().setTextAlign('left').run()}>
                    <PiTextAlignLeftBold />
                </ToolButton>
                <ToolButton title="Centralizar" active={s.center} onClick={() => editor.chain().focus().setTextAlign('center').run()}>
                    <PiTextAlignCenterBold />
                </ToolButton>
                <ToolButton title="Alinhar à direita" active={s.right} onClick={() => editor.chain().focus().setTextAlign('right').run()}>
                    <PiTextAlignRightBold />
                </ToolButton>
                <Divider />
                <ToolButton
                    title="Link"
                    active={s.link || panel === 'link'}
                    onClick={() => {
                        setLinkUrl((editor.getAttributes('link').href as string | undefined) ?? '');
                        setPanel(panel === 'link' ? null : 'link');
                    }}
                >
                    <PiLinkBold />
                </ToolButton>
                <ToolButton title="Referência a uma tela do Hub" active={panel === 'feature'} onClick={() => setPanel(panel === 'feature' ? null : 'feature')}>
                    <PiSquaresFourBold />
                </ToolButton>
                <ToolButton title={uploading ? 'Enviando imagem…' : 'Imagem'} disabled={uploading} onClick={() => fileRef.current?.click()}>
                    <PiImageBold className={cn(uploading && 'animate-pulse')} />
                </ToolButton>
                <ToolButton title="Ícones" active={panel === 'icons'} onClick={() => setPanel(panel === 'icons' ? null : 'icons')}>
                    <PiSmileyBold />
                </ToolButton>
                <ToolButton
                    title="Tabela"
                    active={s.table}
                    onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
                >
                    <PiTableBold />
                </ToolButton>
                <ToolButton title="Linha divisória" onClick={() => editor.chain().focus().setHorizontalRule().run()}>
                    <PiMinusBold />
                </ToolButton>
                <DocAiModal open={aiOpen} onClose={() => setAiOpen(false)} editor={editor} title={title} documentId={documentId} />
                <input
                    ref={fileRef}
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/webp"
                    className="hidden"
                    onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) void uploadImage(file);
                        e.target.value = '';
                    }}
                />
            </div>

            {s.table && (
                <div className="flex flex-wrap items-center gap-1 border-t border-gray-100 px-2 py-1 text-[11px] dark:border-gray-800">
                    <span className="me-1 font-semibold text-gray-400">Tabela:</span>
                    {[
                        ['+ linha', () => editor.chain().focus().addRowAfter().run()],
                        ['+ coluna', () => editor.chain().focus().addColumnAfter().run()],
                        ['− linha', () => editor.chain().focus().deleteRow().run()],
                        ['− coluna', () => editor.chain().focus().deleteColumn().run()],
                        ['Apagar tabela', () => editor.chain().focus().deleteTable().run()],
                    ].map(([label, fn]) => (
                        <button
                            key={label as string}
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={fn as () => void}
                            className="rounded px-1.5 py-0.5 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                            {label as string}
                        </button>
                    ))}
                </div>
            )}

            {panel === 'icons' && (
                <div className="flex flex-wrap gap-1 border-t border-gray-100 px-2 py-2 dark:border-gray-800">
                    {ICONS.map((icon) => (
                        <button
                            key={icon}
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => editor.chain().focus().insertContent(icon + ' ').run()}
                            className="flex size-8 items-center justify-center rounded-md text-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                            {icon}
                        </button>
                    ))}
                </div>
            )}

            {panel === 'link' && (
                <div className="flex items-center gap-2 border-t border-gray-100 px-2 py-2 dark:border-gray-800">
                    <input
                        autoFocus
                        value={linkUrl}
                        onChange={(e) => setLinkUrl(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                applyLink();
                            }
                        }}
                        placeholder="https://… ou /estoque/consulta (vazio remove o link)"
                        className="h-8 flex-1 rounded-md border border-gray-200 bg-white px-2 text-sm dark:border-gray-700 dark:bg-gray-900"
                    />
                    <button type="button" onClick={applyLink} className="h-8 rounded-md bg-primary px-3 text-xs font-semibold text-white">
                        Aplicar
                    </button>
                </div>
            )}

            {panel === 'feature' && (
                <div className="max-h-56 overflow-y-auto border-t border-gray-100 px-2 py-2 dark:border-gray-800">
                    <p className="mb-1.5 text-[11px] text-gray-400">Insere um link para a tela. Com texto selecionado, o texto vira o link.</p>
                    {(nav.data ?? []).map((group) => (
                        <div key={group.group} className="mb-2">
                            <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">{group.label}</p>
                            <div className="flex flex-wrap gap-1">
                                {group.items.map((item) => (
                                    <button
                                        key={item.href}
                                        type="button"
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => insertFeature(item.name, item.href)}
                                        className="rounded-full border border-gray-200 px-2 py-0.5 text-xs text-gray-700 hover:border-primary hover:text-primary dark:border-gray-700 dark:text-gray-200"
                                    >
                                        {item.name}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function DocEditor({ value, onChange, title = '', documentId }: Props) {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: { levels: [2, 3] },
                link: { openOnClick: false, autolink: true, HTMLAttributes: { rel: 'noopener noreferrer' } },
            }),
            Image.configure({ inline: false }),
            TableKit.configure({ table: { resizable: false } }),
            Placeholder.configure({ placeholder: 'Escreva aqui… Use T1/T2 para títulos, a lista numerada para o passo a passo e a caixa de destaque para dicas.' }),
            TextAlign.configure({ types: ['heading', 'paragraph'] }),
            Highlight,
        ],
        content: value,
        immediatelyRender: false,
        onUpdate: ({ editor: e }) => onChange(e.getHTML()),
        editorProps: {
            attributes: { class: 'doc-content doc-editor min-h-[420px] px-5 py-4 focus:outline-none' },
        },
    });

    if (!editor) {
        return <div className="min-h-[420px] rounded-xl border border-gray-200 dark:border-gray-700" />;
    }

    return (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
            <Toolbar editor={editor} title={title} documentId={documentId} />
            <EditorContent editor={editor} />
        </div>
    );
}
